<template>
  <div>
    <div class="d-flex justify-content-between align-items-center mb-3">
      <h2 class="mb-0">
        Disputes History
      </h2>
      <div class="d-flex align-items-center">
        <date-range-picker
          v-model="dateRange"
          opens="left"
          class="mr-2"
          :locale-data="{ firstDay: 1, format: 'mediumDate' }"
          :ranges="dateRanges"
          @update="handleFetchDisputes"
        >
          <template #input>
            <div
              style="min-width: 210px;"
              class="text-right"
            >
              <CIcon
                name="cil-calendar"
                class="mr-1"
              />
              {{ formatDate(selectedDateFrom) }} - {{ formatDate(selectedDateTo) }}
            </div>
          </template>
        </date-range-picker>
        <ButtonLoading
          color="primary"
          :loading="loading"
          @click="handleFetchDisputes"
        >
          <CIcon
            name="cil-filter"
            class="mr-1"
          /> Filter
        </ButtonLoading>
      </div>
    </div>

    <div class="row mb-4">
      <!-- KPI: Dispute Requests -->
      <div class="col-sm-6 col-lg-3">
        <CCard class="mb-3 mb-lg-0 border-0 shadow-sm overflow-hidden">
          <div class="p-3 d-flex align-items-center bg-gradient-info text-white h-100">
            <div class="mr-3">
              <CIcon
                name="cil-list-numbered"
                height="36"
              />
            </div>
            <div>
              <div class="text-value-lg">
                {{ disputes.length }}
              </div>
              <div class="text-uppercase font-weight-bold small">
                Dispute Requests
              </div>
            </div>
          </div>
        </CCard>
      </div>

      <!-- KPI: Affected Calls -->
      <div class="col-sm-6 col-lg-3">
        <CCard class="mb-3 mb-lg-0 border-0 shadow-sm overflow-hidden">
          <div class="p-3 d-flex align-items-center bg-gradient-primary text-white h-100">
            <div class="mr-3">
              <CIcon
                name="cil-phone"
                height="36"
              />
            </div>
            <div>
              <div class="text-value-lg">
                {{ totalCallsCount }}
              </div>
              <div class="text-uppercase font-weight-bold small">
                Affected Calls
              </div>
            </div>
          </div>
        </CCard>
      </div>

      <!-- KPI: Total Adjustment -->
      <div class="col-sm-6 col-lg-3">
        <CCard class="mb-3 mb-lg-0 border-0 shadow-sm overflow-hidden">
          <div class="p-3 d-flex align-items-center bg-gradient-success text-white h-100">
            <div class="mr-3">
              <CIcon
                name="cil-dollar"
                height="36"
              />
            </div>
            <div>
              <div class="text-value-lg">
                {{ formatAmount(totalAdjustment) }}
              </div>
              <div class="text-uppercase font-weight-bold small">
                Total Adjustment
              </div>
            </div>
          </div>
        </CCard>
      </div>

      <!-- KPI: Pending Review -->
      <div class="col-sm-6 col-lg-3">
        <CCard class="mb-0 border-0 shadow-sm overflow-hidden">
          <div class="p-3 d-flex align-items-center bg-gradient-warning text-white h-100">
            <div class="mr-3">
              <CIcon
                name="cil-clock"
                height="36"
              />
            </div>
            <div>
              <div class="text-value-lg">
                {{ pendingCount }}
              </div>
              <div class="text-uppercase font-weight-bold small">
                Pending Review
              </div>
            </div>
          </div>
        </CCard>
      </div>
    </div>

    <!-- Charts Section -->
    <div class="row mb-3">
      <div class="col-md-8">
        <CCard class="h-100 mb-0 shadow-sm">
          <CCardHeader class="bg-white d-flex justify-content-between align-items-center">
            <strong>Dispute Flow</strong>
          </CCardHeader>
          <CCardBody>
            <SankeyChart 
              :data="sankeyData" 
              :currency="true" 
              style="height: 450px;"
            />
          </CCardBody>
        </CCard>
      </div>
      
      <div class="col-md-4 d-flex flex-column">
        <CCard class="flex-grow-1 mb-3 shadow-sm">
          <CCardBody class="p-3">
            <div class="text-uppercase text-muted small font-weight-bold mb-3">
              Top Sellers
            </div>
            <div
              v-if="topSellers && topSellers.length > 0"
              class="d-flex align-items-center"
            >
              <div style="width: 120px; height: 120px;">
                <CChartPie
                  :datasets="sellerChartData.datasets"
                  :labels="sellerChartData.labels"
                  :options="pieOptions"
                  style="height: 100%; width: 100%;"
                />
              </div>
              <div
                class="flex-grow-1 ml-3 overflow-hidden"
                style="max-height: 120px; overflow-y: auto;"
              >
                <div
                  v-for="(item, index) in topSellers"
                  :key="index"
                  class="d-flex align-items-center mb-1 small text-muted"
                >
                  <span
                    class="d-inline-block mr-2 rounded-circle"
                    :style="{width: '8px', height: '8px', minWidth: '8px', backgroundColor: chartColors[index % chartColors.length]}"
                  />
                  <span
                    class="text-truncate"
                    :title="item.name"
                  >{{ item.name }}</span>
                </div>
              </div>
            </div>
            <div
              v-else
              class="text-center text-muted p-4"
            >
              No data
            </div>
          </CCardBody>
        </CCard>

        <CCard class="flex-grow-1 mb-0 shadow-sm">
          <CCardBody class="p-3">
            <div class="text-uppercase text-muted small font-weight-bold mb-3">
              Top Buyers
            </div>
            <div
              v-if="topBuyers && topBuyers.length > 0"
              class="d-flex align-items-center"
            >
              <div style="width: 120px; height: 120px;">
                <CChartPie
                  :datasets="buyerChartData.datasets"
                  :labels="buyerChartData.labels"
                  :options="pieOptions"
                  style="height: 100%; width: 100%;"
                />
              </div>
              <div
                class="flex-grow-1 ml-3 overflow-hidden"
                style="max-height: 120px; overflow-y: auto;"
              >
                <div
                  v-for="(item, index) in topBuyers"
                  :key="index"
                  class="d-flex align-items-center mb-1 small text-muted"
                >
                  <span
                    class="d-inline-block mr-2 rounded-circle"
                    :style="{width: '8px', height: '8px', minWidth: '8px', backgroundColor: chartColors[index % chartColors.length]}"
                  />
                  <span
                    class="text-truncate"
                    :title="item.name"
                  >{{ item.name }}</span>
                </div>
              </div>
            </div>
            <div
              v-else
              class="text-center text-muted p-4"
            >
              No data
            </div>
          </CCardBody>
        </CCard>
      </div>
    </div>

    <CCard>
      <CCardBody>
        <div
          v-if="false"
          class="row mb-4"
        >
          <!-- Date picker moved to header -->
        </div>

        <CDataTable
          :items="disputes"
          :fields="fields"
          :loading="loading"
          hover
          striped
          border
          :items-per-page="10"
          :pagination="{ align: 'center' }"
        >
          <template #date="{ item }">
            <td>
              {{ formatDateTime(item.date) }}
            </td>
          </template>

          <template #initiatedBy="{ item }">
            <td>
              {{ item.initiatedBy }}
            </td>
          </template>

          <template #callCount="{ item }">
            <td class="text-center">
              {{ item.callCount || 0 }}
            </td>
          </template>

          <template #amount="{ item }">
            <td class="text-right">
              {{ formatAmount(item.totalAmount) }}
            </td>
          </template>

          <template #status="{ item }">
            <td>
              <CBadge :color="getStatusColor(item.status)">
                {{ item.status }}
              </CBadge>
            </td>
          </template>

          <template #actions="{ item }">
            <td>
              <CButton
                color="info"
                size="sm"
                @click="viewDetails(item)"
              >
                Details
              </CButton>
            </td>
          </template>
        </CDataTable>
      </CCardBody>
    </CCard>

    <!-- Dispute Details Modal -->
    <CModal
      :show.sync="showDetailsModal"
      size="xl"
      :close-on-backdrop="false"
    >
      <template #header>
        <h5 class="modal-title">
          Dispute Details - {{ selectedDispute ? selectedDispute.id : '' }}
        </h5>
        <CButtonClose @click="showDetailsModal = false" />
      </template>

      <div v-if="selectedDispute">
        <CRow class="mb-3">
          <CCol md="6">
            <div><strong>Initiated By:</strong> {{ extractName(selectedDispute.initiatedBy) }}</div>
            <div class="mt-2">
              <strong>Date Reviewed:</strong> {{ formatDateTime(selectedDispute.date) }}
            </div>
            <div class="mt-2">
              <strong>Reason:</strong> {{ selectedDispute.reason }}
            </div>
          </CCol>
          <CCol md="6">
            <div>
              <strong>Status:</strong> 
              <CBadge
                :color="getStatusColor(selectedDispute.status)"
                class="ml-2"
              >
                {{ selectedDispute.status }}
              </CBadge>
            </div>
            <div class="mt-2">
              <strong>Total Adjustment:</strong> {{ formatAmount(selectedDispute.totalAmount) }}
            </div>
          </CCol>
        </CRow>

        <h6 class="mt-4 mb-3">
          Affected Calls ({{ affectedCalls.length }})
        </h6>
        <CDataTable
          :items="affectedCalls"
          :fields="callFields"
          hover
          striped
          border
          small
          :items-per-page="5"
          :pagination="{ align: 'center' }"
        >
          <template #duration="{ item }">
            <td>
              {{ convertDurationInSecsToTimeFormat(item.duration) }}
            </td>
          </template>
          <template #amount="{ item }">
            <td>
              {{ formatAmount(item.amount) }}
            </td>
          </template>
          <template #date="{ item }">
            <td>
              {{ moment(item.date).format('MMM DD HH:mm:ss A') }}
            </td>
          </template>
        </CDataTable>
      </div>

      <template #footer>
        <CButton
          color="secondary"
          @click="showDetailsModal = false"
        >
          Close
        </CButton>
      </template>
    </CModal>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { CChartPie } from '@coreui/vue-chartjs'
import DateRangePicker from 'vue2-daterange-picker'
import 'vue2-daterange-picker/dist/vue2-daterange-picker.css'
import moment from 'moment-timezone'
import { showErrorMessage } from '@/notification-utils'
import SankeyChart from '@/components/Charts/SankeyChart.vue'
import CallLogsApi from '@/api/call-logs.api'
import ButtonLoading from '@/components/common/ButtonLoading.vue'

// Helper functions 
const formatDate = (date) => {
  return moment(date).format('MMM DD, YYYY')
}

const formatDateTime = (date) => {
  return moment(date).format('MMM DD, YYYY, h:mm A')
}

const formatAmount = (amount) => {
  if (amount === null || amount === undefined) {
    return 'N/A'
  }

  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount)
}

const convertDurationInSecsToTimeFormat = (durationInSeconds) => {
  if (!durationInSeconds) {
    return '00:00:00' 
  }

  const duration = moment.duration(durationInSeconds, 'seconds')
  const hours = Math.floor(duration.asHours())
  const mins = Math.floor(duration.minutes())
  const secs = Math.floor(duration.seconds())
  return `${hours < 10 ? '0' + hours : hours}:${mins < 10 ? '0' + mins : mins}:${secs < 10 ? '0' + secs : secs}`
}

const getDefaultDateRange = () => {
  const end = moment().endOf('day').toDate()
  const start = moment().subtract(29, 'days').startOf('day').toDate()
  return { startDate: start, endDate: end }
}

export default {
  name: 'DisputesHistoryComponent',
  components: {
    DateRangePicker, SankeyChart, ButtonLoading, CChartPie
  },
  props: {
    active: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      dateRange: getDefaultDateRange(),
      loading: false,
      fields: [
        { key: 'id', label: 'Request ID' },
        { key: 'date', label: 'Date Requested' },
        { key: 'initiatedBy', label: 'Initiated By' },
        { key: 'reason', label: 'Reason' },
        { key: 'callCount', label: 'Calls', _classes: 'text-center' },
        { key: 'amount', label: 'Total Adjustment', _classes: 'text-right' },
        { key: 'status', label: 'Status' },
        { key: 'actions', label: '', _style: 'width: 1%' }
      ],
      callFields: [
        { key: 'callId', label: 'Call ID' },
        { key: 'date', label: 'Call Date' },
        { key: 'buyerAccount', label: 'Buyer Account' },
        { key: 'sellerAccount', label: 'Seller Account' },
        { key: 'duration', label: 'Duration' },
        { key: 'category', label: 'Category' },
        { key: 'prevPayout', label: 'Old Payout' },
        { key: 'newPayout', label: 'New Payout' },
        { key: 'adjustment', label: 'Adjustment' }
      ],
      topWidgetFields: [
        { key: 'name', label: 'Account', _style: 'width: 60%;' },
        { key: 'percent', label: 'Disputed Calls', _style: 'width: 40%;' }
      ],
      showDetailsModal: false,
      selectedDispute: null,
      sankeyCallDetails: [],
      dataLoaded: false,
      chartColors: ['#321fdb', '#3399ff', '#f9b115', '#e55353', '#2eb85c', '#636f83', '#ced2d8', '#ebedef', '#E91E63', '#9C27B0']
    }
  },
  computed: {
    ...mapGetters('disputes', { disputes: 'formattedDisputes' }),
    
    pieOptions() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        legend: {
          display: false
        },
        layout: {
          padding: {
            left: 0,
            right: 0,
            top: 0,
            bottom: 0
          }
        },
        hover: {
          animationDuration: 0
        },
        animation: {
          duration: 500,
          easing: "easeOutQuart",
          onComplete: function () {
            var ctx = this.chart.ctx
            ctx.font = "bold 10px sans-serif"
            ctx.textAlign = 'center'
            ctx.textBaseline = 'middle'
                
            this.data.datasets.forEach((dataset, i) => {
              var meta = this.chart.getDatasetMeta(i)
              if (!meta.hidden) {
                meta.data.forEach(function (element, index) {
                  var dataString = dataset.data[index]
                  var value = dataset.data[index]
                  var total = dataset.data.reduce((a, b) => a + b, 0)
                  var percent = total > 0 ? (value / total * 100) : 0
                            
                  if (percent < 5) {
                    return 
                  }

                  // Just to be safe, check if model exists
                  var model = element._model
                  if (!model) {
                    return 
                  }
                            
                  var mid_radius = model.innerRadius + (model.outerRadius - model.innerRadius)/2
                  var start_angle = model.startAngle
                  var end_angle = model.endAngle
                  var mid_angle = start_angle + (end_angle - start_angle)/2

                  var x = mid_radius * Math.cos(mid_angle)
                  var y = mid_radius * Math.sin(mid_angle)

                  ctx.fillStyle = '#fff'
                  ctx.fillText(Math.round(percent) + '%', model.x + x, model.y + y)
                })
              }
            })
          }
        }
      }
    },

    sellerChartData() {
      if (!this.topSellers || this.topSellers.length === 0) {
        return { labels: [], datasets: [] } 
      }
      
      return {
        labels: this.topSellers.map(i => i.name),
        datasets: [{
          data: this.topSellers.map(i => i.count),
          backgroundColor: this.chartColors
        }]
      }
    },

    buyerChartData() {
      if (!this.topBuyers || this.topBuyers.length === 0) {
        return { labels: [], datasets: [] } 
      }
      
      return {
        labels: this.topBuyers.map(i => i.name),
        datasets: [{
          data: this.topBuyers.map(i => i.count),
          backgroundColor: this.chartColors
        }]
      }
    },

    dateRanges() {
      const now = moment()
      return {
        'Today': [now.clone().startOf('day').toDate(), now.clone().endOf('day').toDate()],
        'Yesterday': [now.clone().subtract(1, 'days').startOf('day').toDate(), now.clone().subtract(1, 'days').endOf('day').toDate()],
        'Last 7 Days': [now.clone().subtract(6, 'days').startOf('day').toDate(), now.clone().endOf('day').toDate()],
        'Last 30 Days': [now.clone().subtract(29, 'days').startOf('day').toDate(), now.clone().endOf('day').toDate()],
        'This Month': [now.clone().startOf('month').toDate(), now.clone().endOf('month').toDate()],
        'Last Month': [now.clone().subtract(1, 'month').startOf('month').toDate(), now.clone().subtract(1, 'month').endOf('month').toDate()],
        'Last 6 Months': [now.clone().subtract(6, 'months').startOf('day').toDate(), now.clone().endOf('day').toDate()],
        'All Time': [moment('2010-01-01').toDate(), now.clone().endOf('day').toDate()]
      }
    },
    
    selectedDateFrom() {
      return this.dateRange.startDate
    },
    selectedDateTo() {
      return this.dateRange.endDate
    },
    dateFrom() {
      return moment(this.dateRange.startDate).format('YYYY-MM-DD')
    },
    dateTo() {
      return moment(this.dateRange.endDate).format('YYYY-MM-DD')
    },
    
    totalAdjustment() {
      if (!this.disputes || this.disputes.length === 0) {
        return 0
      }

      return this.disputes.reduce((sum, d) => sum + (Math.abs(d.totalAmount) || 0), 0)
    },

    pendingCount() {
      if (!this.disputes) {
        return 0
      }

      return this.disputes.filter(d => d.status && d.status.toLowerCase().includes('pending')).length
    },
    
    currentDisputeCalls() {
      if (!this.selectedDispute) {
        return [] 
      }

      const dispute = this.selectedDispute
      const raw = dispute.raw || dispute // Fallback to dispute itself if raw undefined
        
      // 1. If we have detailed callsAffected, use them.
      let calls = []
      if (raw.callsAffected && raw.callsAffected.length > 0) {
        calls = raw.callsAffected
      }
        
      // 2. If we fetched details for Sankey, we might find them there too by ID
      if (calls.length === 0 && raw.callIds && raw.callIds.length > 0 && this.sankeyCallDetails.length > 0) {
        calls = this.sankeyCallDetails.filter(c => raw.callIds.includes(c.callId))
      }
        
      // 3. Stub if only IDs
      if (calls.length === 0 && raw.callIds && raw.callIds.length > 0) {
        calls = raw.callIds.map(id => ({ callId: id }))
      }
        
      return calls
    },

    affectedCalls() {
      return this.currentDisputeCalls.map(c => {
        let detail = c
        if ((!c.sellerAccount && !c.sellerAccountName) && this.sankeyCallDetails.length > 0) {
          const found = this.sankeyCallDetails.find(fx => fx.callId === c.callId)
          if (found) {
            detail = found
          }
        }

        // Resolve Old Payout (Original Payout from Call Log)
        let oldPayout = detail.oldPayout !== undefined ? detail.oldPayout : detail.oldPayoutAmount
        if (oldPayout === undefined) {
          // Fallback: checks for standard call log fields if dispute-specific fields are missing
          // In this context, the call log 'payoutAmount' IS the old payout before dispute
          if (detail.payoutAmount !== undefined) {
            oldPayout = detail.payoutAmount 
          } else if (detail.payout !== undefined) {
            oldPayout = detail.payout 
          } else if (detail.winningBid !== undefined) {
            oldPayout = detail.winningBid 
          }
        }

        // Resolve New Payout (Proposed Payout from Dispute)
        let newPayout = detail.newPayout !== undefined ? detail.newPayout : detail.newPayoutAmount
             
        // Fallback: use dispute-level newPayoutAmount if specific call detail is missing
        if (newPayout === undefined && this.selectedDispute && this.selectedDispute.raw) {
          if (this.selectedDispute.raw.newPayoutAmount !== undefined) {
            newPayout = this.selectedDispute.raw.newPayoutAmount
          }
        }
             
        // Ensure we have numbers for calculation
        const valOld = Number(oldPayout) || 0
        const valNew = Number(newPayout) || 0

        let adjustment = detail.deltaPayout
        if (adjustment === undefined) {
          adjustment = valNew - valOld
        }

        return {
          callId: detail.callId,
          date: detail.callTime || detail.callDate || 'N/A', 
          buyerAccount: this.extractName(detail.buyerAccount || detail.buyerAccountName || detail.buyerName) || 'N/A',
          sellerAccount: this.extractName(detail.sellerAccount || detail.sellerAccountName || detail.sellerName) || 'N/A',
          duration: detail.duration !== undefined ? detail.duration : 0,
          category: detail.categoryName || detail.category || 'N/A',
          prevPayout: formatAmount(oldPayout),
          newPayout: formatAmount(newPayout),
          adjustment: formatAmount(adjustment),
          amount: adjustment
        }
      })
    },

    sankeyData() {
      const flows = {} 

      this.disputes.forEach(dispute => {
        const raw = dispute.raw || dispute // Handle if dispute is the raw object
        let calls = raw.callsAffected || []
        
        // Check if callsAffected has meaningful data (accounts)
        const hasDetails = calls.length > 0 && calls.some(c => c.sellerAccount || c.sellerAccountName || c.buyerAccount || c.buyerAccountName || c.BuyerAccountName || c.SellerAccountName)

        if (!hasDetails && raw.callIds && raw.callIds.length > 0) {
          calls = this.sankeyCallDetails.filter(c => raw.callIds.includes(c.callId))
        }
        
        if (calls.length > 0) {
          const callsPerPair = {} // Key: "Seller|Buyer", Value: { count: 0, amount: 0 }
          
          calls.forEach(call => {
            // Handle both camelCase (JSON) and PascalCase (C# Models) prop names just in case
            const seller = this.extractName(call.sellerAccount || call.sellerAccountName || call.SellerAccountName) || 'Unknown Seller'
            const buyer = this.extractName(call.buyerAccount || call.buyerAccountName || call.BuyerAccountName || dispute.initiatedBy) || 'Unknown Buyer'
            const key = `${seller}|${buyer}`
             
            if (!callsPerPair[key]) {
              callsPerPair[key] = { count: 0, amount: 0 }
            }

            callsPerPair[key].count++
          })
          
          const totalCallsInDispute = calls.length
          const disputeTotal = (dispute.totalAmount !== null && dispute.totalAmount !== undefined) ? Math.abs(dispute.totalAmount) : 0
          
          Object.entries(callsPerPair).forEach(([key, data]) => {
            if (!flows[key]) {
              flows[key] = { value: 0, count: 0 }
            }

            flows[key].count += data.count
            flows[key].value += (data.count / totalCallsInDispute) * disputeTotal
          })

        } else {
          const seller = 'Unknown Seller'
          const buyer = this.extractName(dispute.initiatedBy) || 'Unknown Buyer'
          const amount = Math.abs(dispute.totalAmount || 0)
          
          const key = `${seller}|${buyer}`
          if (!flows[key]) {
            flows[key] = { value: 0, count: 0 }
          }

          flows[key].value += amount
          flows[key].count += (raw.callsCount || (raw.callIds && raw.callIds.length) || 1)
        }
      })
      
      return Object.entries(flows).map(([key, data]) => {
        const [source, target] = key.split('|')
        return { source, target, value: data.value, count: data.count }
      })
    },
    
    totalCallsCount() {
      return this.sankeyData.reduce((sum, item) => sum + item.count, 0)
    },
    
    topSellers() {
      const counts = {}
      const total = this.totalCallsCount
      if (total === 0) {
        return []
      }
        
      this.sankeyData.forEach(item => {
        counts[item.source] = (counts[item.source] || 0) + item.count
      })
        
      return Object.entries(counts)
        .map(([name, count]) => ({ name, count, percent: Math.round(count / total * 100) }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 10)
    },
    
    topBuyers() {
      const counts = {}
      const total = this.totalCallsCount
      if (total === 0) {
        return []
      }
        
      this.sankeyData.forEach(item => {
        counts[item.target] = (counts[item.target] || 0) + item.count
      })
        
      return Object.entries(counts)
        .map(([name, count]) => ({ name, count, percent: Math.round(count / total * 100) }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 10)
    },
  },
  watch: {
    active: {
      immediate: true,
      handler(val) {
        if (val && !this.dataLoaded) {
          this.handleFetchDisputes()
          this.dataLoaded = true
        }
      }
    }
  },
  methods: {
    ...mapActions('disputes', ['fetchDisputes']),
    formatDate,
    formatDateTime,
    formatAmount,
    convertDurationInSecsToTimeFormat,

    async handleFetchDisputes() {
      this.loading = true
      try {
        await this.fetchDisputes({
          dateFrom: this.dateFrom,
          dateTo: this.dateTo,
        })
        await this.fetchSankeyCallDetails()
      } catch (error) {
        showErrorMessage('Failed to fetch disputes history')
        console.error(error)
      } finally {
        this.loading = false
      }
    },

    async fetchSankeyCallDetails() {
      const disputesNeedingDetails = this.disputes.filter(d => {
        const raw = d.raw || {}
        const hasDetails = raw.callsAffected && raw.callsAffected.length > 0 && raw.callsAffected.some(c => c.sellerAccount || c.sellerAccountName)
        return !hasDetails && raw.callIds && raw.callIds.length > 0
      })

      if (disputesNeedingDetails.length === 0) {
        return
      }

      disputesNeedingDetails.sort((a, b) => new Date(a.date) - new Date(b.date))

      const batches = []
      let currentBatch = {
        ids: new Set(),
        startDate: null,
        endDate: null
      }
      
      const MAX_DAYS = 90 
      
      disputesNeedingDetails.forEach(d => {
        const raw = d.raw || {}
        const dDate = moment(d.date)
        const dStart = dDate.clone().subtract(60, 'days')
        const dEnd = dDate.clone().add(1, 'days')
        
        if (!currentBatch.ids.size) {
          currentBatch.startDate = dStart
          currentBatch.endDate = dEnd
          if (raw.callIds) {
            raw.callIds.forEach(id => currentBatch.ids.add(id))
          }
        } else {
          const newStart = moment.min(currentBatch.startDate, dStart)
          const newEnd = moment.max(currentBatch.endDate, dEnd)
          const duration = newEnd.diff(newStart, 'days')
           
          if (duration <= MAX_DAYS) {
            currentBatch.startDate = newStart
            currentBatch.endDate = newEnd
            if (raw.callIds) {
              raw.callIds.forEach(id => currentBatch.ids.add(id))
            }
          } else {
            batches.push(currentBatch)
            currentBatch = {
              ids: new Set(raw.callIds || []),
              startDate: dStart,
              endDate: dEnd
            }
          }
        }
      })
      
      if (currentBatch.ids.size) {
        batches.push(currentBatch)
      }

      try {
        const promises = batches.map(batch => {
          return CallLogsApi.searchForCalls(
            [], 
            [...batch.ids],
            batch.startDate.toDate(),
            batch.endDate.toDate()
          )
        })
        
        const responses = await Promise.all(promises)
        this.sankeyCallDetails = responses.flatMap(r => r.data || [])
      } catch (error) {
        console.error('Failed to fetch sankey call details', error)
      }
    },

    getStatusColor(status) {
      if (!status) {
        return 'secondary'
      }

      switch (String(status).toLowerCase()) {
        case 'approved':
          return 'success'
        case 'rejected':
          return 'danger'
        case 'pending':
          return 'warning'
        case 'completed':
          return 'success'
        case 'pendingreview':
          return 'info'
        default:
          return 'secondary'
      }
    },
    
    moment(date) {
      return moment(date)
    },

    viewDetails(item) {
      this.selectedDispute = item
      this.showDetailsModal = true
    },
    
    extractName(val) {
      if (!val) {
        return null
      }

      if (typeof val === 'string') {
        return val
      }

      if (val.name) {
        return val.name
      } // If it's an object with name property

      return String(val)
    }
  }
}
</script>

<style scoped>
.modal-title {
  font-weight: bold;
}
</style>
